import { TableCard } from './TableCard';
import { useEffect, useMemo, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { IS_DEMO, IS_LOCKED } from '../../apiBase';
import { CallsBadge } from './CallsBadge';
import { CenteredSelect } from './CenteredSelect';
import { PhaseControls } from './PhaseControls';
import { MaterialsTable } from './MaterialsTable';
import { ColourwayMaterials, shownReading } from './ColourwayMaterials';
import { MaterialPatches } from './MaterialPatches';
import { cropRings, getListingCrops } from './listingCropsRun';
import { cutCrop, cutRect } from './cutCrop';
import { reasoningTooltip } from './ReasoningBadge';
import { ensureCategories, useCategories } from './catalogCategories';
import { useSubmodelPhotos } from './contextImages';
import { listingImageSrc } from './panels/NoticeProductImages';
import { directionLabel, VIEW_DIRECTIONS } from './viewDirections';
import { getParts, usePartsRun } from './partsRun';
import { usePartIdentities } from './partIdentity';
import { movePieces } from './productPieces';
import { materialText, type MaterialPair } from './variantLabel';
import { useSubmodels } from './listingStore';
import { useSubmodelOptions } from './SubmodelPicker';
import { listingMaterials, partPaints, replacePartMaterials, shareHardware, usePartMaterials } from './partMaterialRun';
import { defaultEffort, writtenPartRows, writtenParts } from './openaiBlenderApi';
import {
  appliedIdOf, applyMaterials, keepHardware, type PartPaint,
  markHardwareApplied, materialsPrompt, newReadingId, readMaterials, startedOf, useMaterialsRun, namesOf,
} from './materialsRun';
import { falError, fetchStatus3D, type Status3D } from './threeDApi';
import { runBtnClass } from './controls';
import { useMaterialParts } from './MaterialPartsPicker';
import { applyHardware, askHardware, hardwarePrompt, useHardwareRun, type HardwarePart } from './defaultTexturesRun';
import { useStandardMaterials } from './standardMaterials';
import { repaintSets } from './hardwareSets';
import { ASTRA_MODEL } from './models';
import { clock, useSince } from './waitClock';
import { useLanguage } from '../../contexts/LanguageContext';

// PHASE 5, FIRST SUBPHASE: WHAT THE PRODUCT IS MADE OF.
//
// Not a property of a 3D model, which is why it is a subphase and not a panel in the modal of
// one. It used to be the first half of the written engine's answer — the same call that wrote
// the bpy opened by stating MATERIAL lines — and that made a colour a property of whichever
// run happened to be pressed: two builds of one chair came out two browns, and a re-run asked
// for a shape correction re-judged the fabric on the way past.
//
// TWO PRESSES, because they are two calls and the second is only worth making once the first
// has an answer to be about. One OpenAI call names the materials and judges the three numbers
// off the photographs; one Astra call per photograph then finds a patch of each material and
// measures it (see texture_stats). The second is re-pressable on its own: a rectangle is a
// judgement, and looking again is the only way to disagree with the first look.
//
// PER SUBMODEL, following the same dropdown the rest of the phase follows: a beige chair and a
// grey one are two products photographed separately.
// HOW MANY CLOSE-UPS OF ONE PART THE CALL IS SHOWN. Two: the second is worth having because the
// first may be in shadow, and the twelfth is a bill. The measurement reads them all.
const SHOTS_PER_PART = 2;

export function MaterialsPanel({ docId, submodel, label, file, locked }: {
  docId: string;
  submodel: string;
  // What the product is CALLED — the selector's own segment. It goes into the question.
  label: string;
  // The manual — the hardware call is shown each part's drawing from its parts list.
  file: Blob;
  locked?: boolean;
}) {
  const { t } = useLanguage();
  // Null until the server has answered: no inputs, so the press stays shut until then.
  const { photos, photosOf } = useSubmodelPhotos(docId);
  const submodels = useSubmodels(docId);
  const options = useSubmodelOptions(docId);
  const snap = useMaterialsRun(docId);
  // A PRESS IS OUT, for this colourway — the badge's clock.
  const reading = snap.reading.has(submodel) || snap.cropping.has(submodel);
  // WHICH READING EACH COLOURWAY'S TABLE SHOWS, by colourway: a press puts its own on screen in
  // all of them, and the one Apply writes what each shows. Where you are looking, so not stored.
  const [picked, setPicked] = useState<Record<string, string | null>>({});
  const appliedId = appliedIdOf(snap, submodel);
  // THE READING THE BADGE'S ANSWERS AND THE STANDARD MATERIALS ARE ABOUT: the hardware half is
  // filed under every colourway's reading alike (see read), so this colourway's speaks for all.
  const shown = shownReading(snap, submodel, picked[submodel]);
  const waited = useSince(startedOf(snap, submodel));
  const [error, setError] = useState('');

  // THE MODELS THIS SERVER HAS A KEY FOR, and the effort each takes — the same probe the
  // models phase's generator reads.
  const [probe, setProbe] = useState<Status3D | null>(null);
  useEffect(() => {
    void fetchStatus3D().then(setProbe).catch(() => setProbe(null));
  }, []);
  const models = useMemo(
    () => (probe?.openai?.configured ? probe.openai.models : []).map((m) => ({
      value: m.id, label: m.label,
    })),
    [probe],
  );
  const [pickedModel, setPickedModel] = useState('');
  const model = models.some((m) => m.value === pickedModel) ? pickedModel : (models[0]?.value ?? '');
  // How hard it thinks: the model's own default, not a choice offered here.
  const effort = defaultEffort(probe, model);

  // WHICH PHOTOGRAPHS THESE TWO CALLS ARE SHOWN: every one of this colourway that is of a
  // NAMEABLE ANGLE.
  //
  // Not the few ticked in the generator below — a material read off a picture the build never
  // sees is still a material this product is made of, and more views is a better judgement of
  // a colour, which is the opposite of the trade the mesh call makes.
  //
  // BUT NOT THE ONES PHASE 1 CLASSIFIED AS 'other', which is its answer for a picture of no
  // nameable angle: a dimension drawing, a detail crop, two chairs at a table, a room. None of
  // those is a photograph of this object's surfaces under this object's lighting, and each one
  // costs tokens and pulls a colour towards whatever else is in the frame.
  //
  // NOR THE ONES NOBODY HAS CLASSIFIED. Blank and 'other' are different facts — only the
  // second is an answer (see OTHER_DIRECTION) — and they are the same fact HERE: what this
  // call needs is a photograph known to be of a face of the object, and an unread picture is
  // not known to be anything. Tested against the vocabulary rather than for emptiness, so a
  // value from some older classification is out too.
  const inputsOf = (of: { id: number; view?: string }[] | null) => (of ?? [])
    .filter((p) => VIEW_DIRECTIONS.some((v) => v.id === p.view))
    .map((p) => ({ id: p.id, view: p.view }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const inputs = useMemo(() => inputsOf(photos), [photos]);

  // WHAT PHASE 2 SAYS EACH PART IS MADE OF, in this colourway, and which parts are the furniture's
  // own — a zinc-plated screw is the same screw whatever the chair is made of. The ontology is
  // subscribed to because it arrives over the network: read off the module cache before it lands,
  // every category resolves to component and the screws are painted as wood.
  const partMaterials = usePartMaterials(docId);
  const categories = useCategories();


  // WHAT THE PARTS ARE, WAITED FOR — for the two places that must not be one render behind:
  // the call, and the panel that shows what the call will say. A run builds its prompt once
  // and pays for the reply, so "right one render later" is a wrong answer already bought.
  //
  // THE COMPONENTS WITHOUT THEIR MATERIALS: which one each is made of is half of what the call is
  // asked, and telling it the last answer would be asking it to agree. `rows` is which part each
  // numbered one is, for the answer to be filed under.
  //
  // AND THEIR CROPS: Phase 2's, in this colourway's photographs, bar the ones struck out by hand —
  // the first two of each part, cut, as the close-ups the call is shown. It draws a clean area on
  // each, and each close-up carries what that area is measured against: the photograph, the
  // rectangle that was cut, the part's outlines and its size (see runMeasure).
  const listed = listingMaterials(submodels, submodel);
  // `sub`: whichever colourway is being read — the one chosen, or each of them in turn when the
  // press covers them all (see read). Its photographs, so its crops.
  const sending = async (sub: string = submodel) => {
    const items = getParts(docId);
    const cats = await ensureCategories();
    const rows = writtenPartRows(items, cats);
    const parts = writtenParts(items, undefined, cats) ?? [];
    const byPart = getListingCrops(docId);
    const idOf = new Map((photosOf(sub) ?? []).map((p) => [p.url, p.id]));
    const kept = (at: number) => (byPart[at] ?? []).filter((c) => !c.off && idOf.has(c.url));
    const close = rows.flatMap((at, i) => {
      const size = items[at].size;
      const sides = [size?.x, size?.y, size?.z].filter((v): v is number => typeof v === 'number' && v > 0);
      return kept(at).slice(0, SHOTS_PER_PART).map((c) => ({
        part: i + 1,
        label: parts[i]?.name ?? '',
        imageId: idOf.get(c.url)!,
        cut: cutRect(c.bbox),
        outlines: cropRings(c),
        ...(sides.length ? { partMm: Math.max(...sides) } : {}),
        url: c.url,
        bbox: c.bbox,
      }));
    });
    return { parts, rows, close };
  };
  // The close-ups cut, as they travel — and as the prompt panel shows them. One that cannot be cut
  // is left out of both lists, so the two stay in step.
  const cutClose = async <T extends { url: string; bbox: [number, number, number, number] }>(
    close: T[],
  ): Promise<(T & { image: string })[]> => {
    const images = await Promise.all(close.map((c) => cutCrop(c.url, c.bbox)));
    return close.flatMap((c, i) => {
      const image = images[i];
      return image ? [{ ...c, image }] : [];
    });
  };

  // THE HARDWARE, as the Parts column lists it — asked about by the same press, and assigned on
  // apply with the same gestures as a tick in that column (see useMaterialParts).
  const partsOf = useMaterialParts(docId, submodel);
  const hardwareParts: HardwarePart[] = partsOf.parts
    .filter((p) => p.hardware).map((p) => ({ at: p.at, label: p.label }));
  const askingHardware = useHardwareRun(docId).busy;
  const standard = useStandardMaterials(docId);
  // THE HARDWARE IS EVERY COLOURWAY'S: what one colourway has answered, the others are given
  // (see shareHardware) — whenever the parts, their categories or the colourways change.
  const { items } = usePartsRun(docId);
  useEffect(() => { shareHardware(docId); }, [docId, items, categories, submodels]);
  // WHETHER THE HARDWARE HAS ITS ANSWER ALREADY: every piece of it on a material here, and every
  // default material with the surface the call gives it. Then a press may leave it out and read
  // the components alone (see read).
  const hardwareDone = hardwareParts.length > 0
    && hardwareParts.every((p) => p.at in (partMaterials.bySub[submodel] ?? {}))
    && standard.every((m) => m.roughness != null);

  // THE PRESS IS OUT while either half is — for any colourway: a press over all of them is
  // still out while the one on screen has finished.
  const anyReading = submodels.some((s) => snap.reading.has(s.id) || snap.cropping.has(s.id));
  const pressing = reading || anyReading || askingHardware;

  // ONE PRESS, TWO CALLS SIDE BY SIDE, one reading: the components' (the call, then the
  // measurement off the parts it assigned — see readMaterials) and the hardware's (see
  // askHardware). Each files its half under the press's id as it lands, and one failing leaves
  // the other's half standing.
  //
  // OVER ONE COLOURWAY OR ALL OF THEM (`subs`). The components are read per colourway — a beige
  // seat and a grey one are two answers, each from its own photographs and its own listing's
  // materials. The hardware is NOT: a screw is the same screw in every colourway, so it is asked
  // ONCE and the answer filed under every colourway's reading. All of them side by side, under
  // one id, so each colourway's new reading is the same press.
  // AND WITH OR WITHOUT THE HARDWARE (`withHardware`): once it has its answer, re-reading the
  // components need not pay for it again.
  const nameOf = (sub: string) => options.find((o) => o.id === sub)?.label ?? sub;
  const read = async (subs: string[], withHardware = true) => {
    setError('');
    const id = newReadingId();
    setPicked(Object.fromEntries(subs.map((sub) => [sub, id])));
    const many = subs.length > 1;
    const fail = (sub: string, msg: string) => setError(many ? `${nameOf(sub)} · ${msg}` : msg);
    // A COLOURWAY WITH NOTHING TO READ IS SAID, not skipped quietly: no photograph of a nameable
    // angle, or a listing that names no material.
    const ready = subs.filter((sub) => inputsOf(photosOf(sub)).length && listingMaterials(submodels, sub).length);
    const missing = subs.filter((sub) => !ready.includes(sub));
    const hardware = async () => {
      if (!withHardware || !standard.length || !ready.length) return;
      try {
        const said = await askHardware(docId, submodel, ASTRA_MODEL, file, hardwareParts);
        for (const sub of ready) keepHardware(docId, sub, id, said);
        // An answer that placed no piece of hardware is kept, and said.
        if (hardwareParts.length && !said.picks.length) setError(t('notice.3d.matNoParts'));
      } catch (e) {
        setError(falError(e, t('notice.3d.matFailed')));
      }
    };
    const components = async (sub: string) => {
    try {
      const listedSub = listingMaterials(submodels, sub);
      const { parts, rows, close } = await sending(sub);
      const shots = await cutClose(close);
      await readMaterials(docId, sub, inputsOf(photosOf(sub)), model, {
        subject: label,
        effort: effort || undefined,
        parts,
        names: listedSub.map(materialText),
        shots: shots.map((c) => ({ part: c.part, image: c.image })),
        closeUps: shots.map(({ part, label, imageId, cut, outlines, partMm }) => ({
          part, label, imageId, cut, outlines, partMm,
        })),
        // WHICH MATERIAL EACH COMPONENT IS, kept on the reading: every component is reassigned
        // when it is applied, not when it is read (see apply).
        onParts: (said) => {
          const picks: Record<number, MaterialPair> = {};
          for (const [n, name] of Object.entries(said)) {
            const at = rows[Number(n) - 1];
            const pair = listedSub.find((one) => materialText(one) === name);
            if (at != null && pair) picks[at] = pair;
          }
          // AN ANSWER THAT PLACED NO PART is a broken answer, not a statement that nothing is
          // made of anything: the parts stay as they were, and the page says so.
          if (!Object.keys(picks).length) {
            fail(sub, t('notice.3d.matNoParts'));
            return null;
          }
          return { asked: rows, picks };
        },
      }, id);
    } catch (e) {
      fail(sub, falError(e, t('notice.3d.matFailed')));
    }
    };
    await Promise.all([...ready.map(components), hardware()]);
    if (missing.length) {
      setError(t('notice.3d.matSubsMissing').replace('{s}', missing.map(nameOf).join(', ')));
    }
  };


  // AND THE READING'S HARDWARE HALF, ON ITS OWN BUTTON: the standard materials take its surfaces,
  // then every piece of hardware it was asked about takes its material — or goes back to
  // never-asked — each repainted as a tick in the Parts column would repaint it, and the kept
  // hardware sets with them. Its own because it is not this colourway's: hardware and tools are
  // the same in every one (see shareHardware), where the product's colours are this one's alone.
  // When it began, for its clock — the same as the product's apply shows.
  const [hwSince, setHwSince] = useState<number | null>(null);
  const applyingHw = hwSince != null;
  const hwSpent = useSince(hwSince);
  // PUT EVERY COLOURWAY'S READING ON SCREEN ONTO ITS MESHES, one colourway after another: an
  // apply rewrites files, and three at once would be three repaints of the same parts DB.
  const identities = usePartIdentities(items);
  // Each component, and the hardware on a product material (see partPaints).
  const paintsOf = (picks: Record<number, MaterialPair | null> | undefined): PartPaint[] =>
    partPaints(items, picks, (at) => identities[at]?.itemId);
  const toApply = options
    .map((o) => ({ sub: o.id, reading: shownReading(snap, o.id, picked[o.id]) }))
    .filter(({ reading }) => reading.materials.length > 0)
    .map(({ sub, reading }) => ({ sub, id: reading.id, said: reading.partPicks }));
  const applying = options.some((o) => snap.applying.has(o.id));
  const [applyFrom, setApplyFrom] = useState<number | null>(null);
  const spent = useSince(applyFrom);
  const [report, setReport] = useState('');
  const applyAll = async () => {
    setError('');
    setReport('');
    setApplyFrom(Date.now());
    const lines: string[] = [];
    try {
      for (const { sub, id, said } of toApply) {
        try {
          // THE READING'S PARTS FIRST: every component takes the material it was given, and the
          // product's pieces move to match before the repaint.
          const picks = said
            ? replacePartMaterials(docId, sub, said.asked, said.picks)
            : partMaterials.bySub[sub];
          const out = await applyMaterials(
            docId, sub, id, paintsOf(picks),
            said ? () => movePieces(docId, sub, picks ?? {}) : undefined,
          );
          lines.push(`${nameOf(sub)}: ${t('notice.3d.matApplied')
            .replace('{n}', String(out.applied))
            .replace('{s}', out.skipped.length ? ` (${t('notice.3d.matSkipped')
              .replace('{n}', String(out.skipped.length))})` : '')}`);
        } catch (e) {
          lines.push(`${nameOf(sub)}: ${falError(e, t('notice.3d.matApplyFailed'))}`);
        }
      }
    } finally {
      setApplyFrom(null);
      // ONE LINE PER COLOURWAY (Louis 2026-10-28): four of them run together were one paragraph.
      setReport(lines.join('\n'));
    }
  };

  const applyHw = async () => {
    const hw = shown.hardware;
    if (!hw) return;
    setError('');
    setHwSince(Date.now());
    try {
      applyHardware(docId, hw);
      for (const part of partsOf.parts.filter((p) => hw.asked.includes(p.at))) {
        const got = hw.picks.filter((one) => one.at === part.at);
        partsOf.put(part, got.map((one) => one.pair), namesOf(got));
      }
      await repaintSets(docId, submodel);
      markHardwareApplied(docId, shown.id);
    } catch (e) {
      setError(falError(e, t('notice.3d.matApplyFailed')));
    } finally {
      setHwSince(null);
    }
  };

  // EVERY COLOURWAY AT ONCE, side by side — each its own photographs and its own listing's
  // materials, all on the page under (see ColourwayMaterials). With the hardware, or without it
  // once it has its answer.
  const allSubs = options.map((o) => o.id);
  const runOptions = hardwareDone ? [false, true].map((hw) => ({
    key: hw ? 'hw' : 'parts',
    label: t(hw ? 'notice.3d.matRunWithHardware' : 'notice.3d.matRunPartsOnly'),
    onRun: () => void read(allSubs, hw),
  })) : [];
  // Something to read in at least one colourway: a photograph of a nameable angle, and materials.
  const anyReady = allSubs.some((sub) => inputsOf(photosOf(sub)).length && listingMaterials(submodels, sub).length);
  // AND WHY A COLOURWAY CANNOT BE, said under the badge rather than left to a grey Run. Not
  // before the server has said which photographs it holds (photosOf is null until then).
  const noPhotos = allSubs.filter((sub) => photosOf(sub) && !inputsOf(photosOf(sub)).length);
  const noMaterials = allSubs.filter((sub) => !listingMaterials(submodels, sub).length);

  return (
    <div className="flex flex-col gap-6">
      <PhaseControls>
        {/* THE MODEL, where there is a choice of one — none is offered while the server has
            one, and the prompt panel names it. How hard it thinks is not asked: the model's
            default (see defaultEffort). */}
        <div className="flex flex-col items-center gap-3">
          {!IS_DEMO && models.length > 1 && (
            <CenteredSelect
              value={model}
              options={models}
              onChange={setPickedModel}
              ariaLabel={t('notice.3d.engine')}
              disabled={pressing}
            />
          )}
          {/* ONE NAMED BADGE, ONE RUN, TWO CALLS: the components' and the hardware's, sent side by
              side and filed as one reading (see read). Its panel walks the two, each its own
              question and — for the reading on screen, which is not always the one applied — its
              own answer. */}
          <CallsBadge
            title={t('notice.3d.matCalls')}
            named
            run={{
              onRun: () => void read(allSubs, true),
              // WHICH COLOURWAYS, AND WHETHER THE HARDWARE TOO — see read. A choice only where
              // there is one: several colourways, or hardware already answered.
              ...(runOptions.length > 1 ? { options: runOptions } : {}),
              // Nothing to judge until the listing has named the materials.
              disabled: !!locked || !model || !anyReady,
              // The run's own start, so leaving the card and coming back does not restart it.
              elapsed: reading ? waited : undefined,
            }}
            entries={[
              {
                key: `components:${submodel}:${model}`,
                label: t('notice.3d.matBadge'),
                load: async () => {
                  const { parts, close } = await sending();
                  const shots = await cutClose(close);
                  return {
                    ...(await materialsPrompt(
                      label, model, inputs.map((one) => one.view || ''), parts,
                      listed.map(materialText), shots.map((c) => c.part),
                    )),
                    // The pictures it is about to be shown, at a size somebody can look at — the
                    // photographs, then the close-ups, numbered as the prompt numbers them.
                    urls: [
                      ...inputs.map((one, i) => ({
                        url: listingImageSrc(one.id),
                        caption: `${i + 1}${one.view ? ` · ${directionLabel(one.view)}` : ''}`,
                      })),
                      ...shots.map((c, i) => ({ url: c.image, caption: `${inputs.length + i + 1} · ${c.label}` })),
                    ],
                  };
                },
                // ITS ANSWER — the reasoning and reply, then the part crops its measurement read,
                // pictures and all (see MaterialPatches).
                answer: shown.reasoning || shown.crops.length ? (
                  <div className="space-y-6">
                    {shown.reasoning && reasoningTooltip(shown.reasoning, shown.said || '', t)}
                    {shown.crops.length > 0 && (
                      <div>
                        <p className="mb-3 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                          {t('notice.3d.matPatchesTitle')}
                        </p>
                        <MaterialPatches answer={shown} inputs={inputs} />
                      </div>
                    )}
                  </div>
                ) : undefined,
              },
              {
                key: `hardware:${submodel}:${JSON.stringify(standard.map((m) => [m.pair, m.rgb]))}:${hardwareParts.length}`,
                label: t('notice.defaultMaterials.texture'),
                load: () => hardwarePrompt(docId, submodel, ASTRA_MODEL, file, hardwareParts),
                answer: shown.hardware && (shown.hardware.reasoning || shown.hardware.raw)
                  ? reasoningTooltip(shown.hardware.reasoning, shown.hardware.raw, t) : undefined,
              },
            ]}
          />
        </div>
      </PhaseControls>

      {(noPhotos.length > 0 || noMaterials.length > 0) && (
        <div className="text-center text-xs text-amber-400">
          {noPhotos.length > 0 && (
            <p>{t('notice.3d.matNoPhotos').replace('{s}', noPhotos.map(nameOf).join(', '))}</p>
          )}
          {noMaterials.length > 0 && (
            <p>{t('notice.3d.matNoMaterials').replace('{s}', noMaterials.map(nameOf).join(', '))}</p>
          )}
        </div>
      )}
      {error && (
        <p className="text-center text-xs text-rose-400">{error}</p>
      )}

      {/* EVERY COLOURWAY'S TABLE, then the standard materials once: those are every colourway's. */}
      <TableCard className="flex flex-col gap-8">
        {options.map((o) => (
          <ColourwayMaterials
            key={o.id} docId={docId} sub={o.id} locked={locked} pressing={pressing}
            picked={picked[o.id] ?? null} onPick={(id) => setPicked((was) => ({ ...was, [o.id]: id }))}
            title={options.length > 1
              ? `${t('notice.3d.listingMaterials')} (${o.label})` : t('notice.3d.listingMaterials')}
          />
        ))}
        {/* ONE APPLY FOR EVERY COLOURWAY: each takes the reading its table shows. */}
        {toApply.length > 0 && (
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => void applyAll()}
              disabled={applying || pressing || IS_LOCKED || !!locked}
              className={runBtnClass}
            >
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {t(applying ? 'notice.3d.matApplying'
                : toApply.every(({ sub, id }) => id === appliedIdOf(snap, sub)) ? 'notice.3d.matApplyAgain' : 'notice.3d.matApply')}
              {applying && <span className="font-normal tabular-nums opacity-60">({clock(spent)})</span>}
            </button>
            {report && <p className="whitespace-pre-line text-center text-xs text-slate-500">{report}</p>}
          </div>
        )}
        <MaterialsTable
          docId={docId} submodel={submodel} answer={shown} applied={shown.id === appliedId}
          locked={locked} only="standard" title={t('notice.3d.defaultMaterials')}
          applyStandard={!!shown.hardware && (
            <button
              type="button"
              onClick={() => void applyHw()}
              disabled={applyingHw || pressing || IS_LOCKED || !!locked}
              className={runBtnClass}
            >
              {applyingHw ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {t(applyingHw ? 'notice.3d.matApplying' : 'notice.3d.matApplyStandard')}
              {applyingHw && <span className="font-normal tabular-nums opacity-60">({clock(hwSpent)})</span>}
            </button>
          )}
        />
      </TableCard>
    </div>
  );
}
